/**
 * Terminal-device binding · Sprint 2 · Part 1.
 *
 * A POS terminal is a physical device that lives in exactly one outlet.
 * We bind the device to an outlet ONCE — the binding is stored in
 * localStorage under the `pos_terminal_binding` key and survives
 * logout, shift close, terminal lock and browser restart.
 *
 * Every subsequent launch goes straight to the PIN screen for the
 * bound outlet. Only staff at that outlet can log in.
 *
 * Rebinding requires:
 *   1. an `outlet_admin` PIN authenticating that decision, AND
 *   2. no shift open on the current binding, AND
 *   3. no unsettled orders on the current binding.
 *
 * `/pos-demo` bypasses binding entirely — see POSTerminal for the
 * fork.
 */

const BINDING_KEY = "pos_terminal_binding";

// Prefix used for the terminal_id issued at binding. `terminal_id`
// travels in every event and order (contract §8) so it must be unique
// and stable per device.
const TERMINAL_ID_PREFIX = "term_";

/** Read the current binding or null. */
export function getBinding() {
  try {
    const raw = window.localStorage.getItem(BINDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.terminal_id || !parsed.outlet_id) return null;
    return parsed;
  } catch (_e) {
    return null;
  }
}

/** Persist a fresh binding for the device. `terminal_id` is generated
 *  here — the terminal_id in the response must NOT change on rebind
 *  because it identifies the physical hardware; instead we mint a
 *  new one only when the device has never been bound. */
export function setBinding({ tenant_id, outlet_id, outlet_name, existing_terminal_id }) {
  const terminal_id = existing_terminal_id
    || `${TERMINAL_ID_PREFIX}${(window.crypto?.randomUUID?.() || Math.random().toString(16).slice(2)).replace(/-/g, "").slice(0, 16)}`;
  const doc = {
    terminal_id,
    tenant_id,
    outlet_id,
    outlet_name: (outlet_name || "").slice(0, 120),
    bound_at: new Date().toISOString(),
  };
  window.localStorage.setItem(BINDING_KEY, JSON.stringify(doc));
  return doc;
}

/** Wipe the binding — used when a rebind is authorised and we've
 *  confirmed no shift is open and no orders are unsettled. */
export function clearBinding() {
  window.localStorage.removeItem(BINDING_KEY);
}

/** ULID-ish idempotency key for a mutating request. The terminal is
 *  where these are minted (contract §8). We use crypto UUID v4 as a
 *  ULID stand-in — the server treats event_id as an opaque string of
 *  8..64 chars, so any high-entropy identifier is fine. */
export function newEventId() {
  const uuid = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return uuid.replace(/-/g, "");
}
